/**
 * Consentmo cookie consent — stop the browser faking a font weight we don't have
 *
 * Consentmo asks for `font-weight: 600` on its button labels, the settings heading and the
 * provider group names, in both layouts (the bottom cookie bar and the preferences modal).
 * Rauschen A ships only 400 and 500 — see scss/base/_fonts.scss — so there is no 600 face to
 * select. CSS font matching falls back to the 500 file, and Chrome then *synthesises* the
 * missing weight by stroking those glyphs, which is why the consent UI reads heavier than any
 * other text on the site.
 *
 * ⚠️ Synthetic bold keeps the original advance widths, so the text does not reflow — measuring
 * the rendered width at 500 vs 600 returns the identical number and looks like a null result.
 * The only reliable tell is the rendered stroke.
 *
 * `font-synthesis-weight: none` is the fix rather than a list of `font-weight: 500` overrides:
 * it states the actual intent (never fake a weight this family doesn't ship), it needs no
 * selector maintenance as the vendor's markup changes, and it cannot miss an element we didn't
 * think to enumerate. Browsers without the property — Safari below 16.4 — simply keep the
 * synthesised weight, which is today's behaviour, so there is nothing to fall back to.
 *
 * ⚠️ Two constraints force this to be JS rather than a rule in scss/:
 *
 * 1. The widget renders inside a shadow root (`<csm-cookie-consent class="csm-cmp-root">`), so
 *    nothing in custom.css reaches it. The root is `open`, which is the only reason this works
 *    at all — a closed root would leave the app's dashboard as the only option. It exposes no
 *    `::part()` hooks and consumes no CSS custom properties, so there is no styling seam either.
 * 2. `!important` is required. Probed against the live widget: the identical rule without it
 *    leaves the computed weight at 600, because Consentmo's own stylesheets load *inside* the
 *    shadow root and win. This is a deliberate break from the house "lean on the cascade" rule
 *    (docs/dev/conventions/scss.md) and applies only to vendor markup we don't control.
 */
const initConsentmo = () => {
  const HOST_TAG = 'csm-cookie-consent';
  const STYLE_ID = 'bfx-consentmo-weight';

  // The widget is injected by an app script well after the theme's own, and can take a moment on
  // a slow connection. Polling is cheap — a querySelector and a property read — and stops the
  // moment it lands.
  const POLL_MS = 200;
  const DEADLINE_MS = 15000;

  // Scoped to the widget's own shadow tree — `*` cannot leak past the shadow boundary.
  const CSS = '*{font-synthesis-weight:none !important}';

  const inject = (host) => {
    const root = host && host.shadowRoot;
    // The element can exist before its shadow root is attached, so a missing root is a
    // "not yet", not a failure — the caller keeps watching.
    if (!root || root.getElementById(STYLE_ID)) return Boolean(root);

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    root.appendChild(style);
    return true;
  };

  // `attachShadow()` fires no mutation of its own. The host's own insertion into the document
  // does wake a light-DOM observer, but if the root is attached a tick later — and everything
  // the app builds after that happens *inside* the shadow tree, where a light-DOM observer sees
  // nothing — there is no second wake-up to retry on. So the observer covers the fast path (the
  // host arriving with its root ready) and a short poll covers the root appearing silently.
  const observer = new MutationObserver(() => attempt());
  let pollId = 0;
  let settled = false;

  const stop = (reason) => {
    if (settled) return;
    settled = true;
    observer.disconnect();
    clearInterval(pollId);

    // A consent widget that never loads is the normal case for a shopper who has already chosen,
    // so silence is correct there. A host that exists but never exposed a root is a real anomaly
    // and the UI keeps its synthesised bold, which is otherwise invisible in QA — so say so.
    if (reason === 'deadline' && document.querySelector(HOST_TAG)) {
      console.warn(
        `bfx-consentmo: <${HOST_TAG}> never exposed a shadow root within ${DEADLINE_MS}ms, so the consent UI keeps the browser's synthesised bold.`
      );
    }
  };

  const attempt = () => {
    if (inject(document.querySelector(HOST_TAG))) stop('injected');
  };

  attempt();
  if (settled) return;

  observer.observe(document.documentElement, { childList: true, subtree: true });
  pollId = setInterval(attempt, POLL_MS);

  // Watching for the life of the page would keep a timer alive on every page a returning shopper
  // opens. Stopping only ever happens after a successful injection or at this deadline.
  setTimeout(() => stop('deadline'), DEADLINE_MS);
};

initConsentmo();
